
import { Component, Prop } from 'vue-property-decorator';
import TableFilterable from '@/components/reports-v3/components/filterables/TableFilterable.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import { COL, getTableName } from '@/worker/db/sqlHelpers';
import gdbx from '@/store/modules/gdbx';
import { PermissionsGroup } from '@/store/models.def';
import { TableItemFormatter } from '@/components/reports-v3/components/elements/charts/helpers/tableItemFormatter';
import SupplierListFilter from '@/components/reports-v3/components/filters/SupplierListFilter.vue';
import settingx from '@/store/modules/settingx';
import IncludeCreditNoteToggle from '@/components/IncludeCreditNoteToggle.vue';
import IncludeDebitNoteToggle from '@/components/IncludeDebitNoteToggle.vue';
import SlPhModuleOnlyToggle from '@/components/SlPhModuleOnlyToggle.vue';
import moment from 'moment';

@Component({
	components: {
		TableFilterable,
		SupplierListFilter,
		IncludeCreditNoteToggle,
		IncludeDebitNoteToggle,
		SlPhModuleOnlyToggle,
	},
})
export default class SharedStockItemPurchaseBySupplierTable extends FilterWidget {
	@Prop({ default: false })
	public forcePhModuleOnly!: boolean;

	public get permissionIds(): PermissionsGroup[] {
		return ['stocks', 'purchases'];
	}

	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
	> = ['dateRange', 'stockItems', 'suppliers'];

	public tableItems: any[] = [];
	public tableFields: any[] = [];
	public selectedSuppliersList: string[] = [];

	public get dateFormatted() {
		return this.selectedDateRange;
	}

	public get exportFileName() {
		const formattedDateRange = [
			moment(this.selectedDateRange[0]).format('DD MMM YY'),
			moment(this.selectedDateRange[1]).format('DD MMM YY'),
		];
		return (
			'Stock Item Purchase By Supplier' +
			'_' +
			formattedDateRange[0] +
			' to ' +
			formattedDateRange[1]
		);
	}

	public get includeCreditNote() {
		return settingx.now.includeCreditNote;
	}

	public get includeDebitNote() {
		return settingx.now.includeDebitNote;
	}

	public get slPhModuleOnly() {
		// If forcePhModuleOnly is true, always use PH module
		return this.forcePhModuleOnly || settingx.now.slPhModuleOnly;
	}

	public get expensiveHook() {
		const {
			selectedSuppliersList,
			selectedDateRange,
			selectedSuppliers,
			selectedStockItems,
			includeCreditNote,
			includeDebitNote,
			slPhModuleOnly,
		} = this;
		return JSON.stringify([
			selectedSuppliersList,
			selectedDateRange,
			selectedSuppliers,
			selectedStockItems,
			includeCreditNote,
			includeDebitNote,
			slPhModuleOnly,
		]);
	}

	public async expensiveCalc() {
		this.generateLoadingText(0.2, 0.3);

		// Build filter clauses manually for cross-table UNION
		const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
		const dateFilter = `TRY_CAST(${COL.DOCDATE} AS BIGINT) BETWEEN ${this.selectedDateRange[0]} AND ${this.selectedDateRange[1]}`;

		const allSupplierLen = (gdbx.allSuppliers && gdbx.allSuppliers.length) || 0;
		let supplierFilter = '';
		if (this.selectedSuppliersList.length > 0 && this.selectedSuppliersList.length !== allSupplierLen) {
			const list = this.selectedSuppliersList.map((s) => `'${escapeStr(s)}'`).join(',');
			supplierFilter = ` AND ${COL.CODE} IN (${list})`;
		}

		const allStockItemsLen = (gdbx.allStockItems && gdbx.allStockItems.length) || 0;
		let stockItemFilter = '';
		if (this.selectedStockItems.length > 0 && this.selectedStockItems.length !== allStockItemsLen) {
			const list = this.selectedStockItems.map((s) => `'${escapeStr(s)}'`).join(',');
			stockItemFilter = ` AND ${COL.ITEMCODE} IN (${list})`;
		}

		const purchaseFilter = `${dateFilter}${supplierFilter}${stockItemFilter}`;
		const prFilter = `${dateFilter}${supplierFilter}${stockItemFilter}`;
		const apBaseFilter = `${dateFilter}${supplierFilter}`;

		// Get table names
		const purchaseTable = getTableName('purchases');
		const prTable = getTableName('purchaseReturn');
		const apTable = getTableName('supplierTrades');
		const stockTable = getTableName('globalStocks');

		// Ensure all tables are loaded
		const jobId = this.currentJobId;
		const queryHead = await this._ensureTablesLoaded([
			'purchases', 'purchaseReturn', 'supplierTrades', 'globalStocks',
		]);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.3, 0.5);

		// Calculate AP Document amount if in AP mode
		let apDocumentAmount = 0;
		if (!this.slPhModuleOnly) {
			const totalsData = await queryHead.rawQuery(`
				WITH ap_totals AS (
					SELECT
						SUM(CASE WHEN ${COL.DOCTYPE} = 'PI' THEN TRY_CAST(${COL.LOCALAMOUNT} AS DOUBLE) ELSE 0 END) as pi_amt,
						SUM(CASE WHEN ${COL.DOCTYPE} = 'SD' THEN TRY_CAST(${COL.LOCALAMOUNT} AS DOUBLE) ELSE 0 END) as sd_amt,
						SUM(CASE WHEN ${COL.DOCTYPE} = 'SC' THEN TRY_CAST(${COL.LOCALAMOUNT} AS DOUBLE) ELSE 0 END) as sc_amt
					FROM ${apTable}
					WHERE ${apBaseFilter}
				),
				ph_totals AS (
					SELECT
						SUM(amount) as ph_amt,
						SUM(pr_amt) as ph_pr_amt
					FROM (
						SELECT TRY_CAST(${COL.AMOUNT} AS DOUBLE) as amount, 0 as pr_amt
						FROM ${purchaseTable} WHERE ${purchaseFilter}
						UNION ALL
						SELECT 0 as amount, TRY_CAST(${COL.AMOUNT} AS DOUBLE) as pr_amt
						FROM ${prTable} WHERE ${prFilter}
					) AS combined
				)
				SELECT
					(SELECT pi_amt FROM ap_totals) as ap_pi,
					(SELECT sd_amt FROM ap_totals) as ap_sd,
					(SELECT sc_amt FROM ap_totals) as ap_sc,
					(SELECT ph_amt FROM ph_totals) as ph_amt,
					(SELECT ph_pr_amt FROM ph_totals) as ph_pr_amt
			`);

			const row = totalsData[0] || {};
			const apPiAmount = Number(row.ap_pi) || 0;
			const apSdAmount = Number(row.ap_sd) || 0;
			const apScAmount = Number(row.ap_sc) || 0;

			const apTotal = apPiAmount
				+ (this.includeDebitNote ? apSdAmount : 0)
				- (this.includeCreditNote ? apScAmount : 0);

			const phTotal = Number(row.ph_amt) || 0;
			const phReturnTotal = this.includeCreditNote ? (Number(row.ph_pr_amt) || 0) : 0;
			const phAmount = phTotal - phReturnTotal;

			apDocumentAmount = apTotal - phAmount;
		}
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.5, 0.9);

		// Per-item data + stock descriptions in parallel
		let stockCodeFilter = '';
		if (this.selectedStockItems.length > 0 && this.selectedStockItems.length !== allStockItemsLen) {
			const list = this.selectedStockItems.map((s) => `'${escapeStr(s)}'`).join(',');
			stockCodeFilter = ` AND ${COL.CODE} IN (${list})`;
		}

		const [itemData, stockItems] = await Promise.all([
			queryHead.rawQuery(`
				SELECT item_code,
					SUM(amount) as amount,
					SUM(quantity) as quantity,
					SUM(sd_amount) as sd_amount,
					SUM(sd_quantity) as sd_quantity,
					SUM(pr_amount) as pr_amount,
					SUM(pr_quantity) as pr_quantity
				FROM (
					SELECT ${COL.ITEMCODE} as item_code,
						CASE WHEN ${COL.DOCTYPE} IN ('PI','CP') THEN TRY_CAST(${COL.AMOUNT} AS DOUBLE) ELSE 0 END as amount,
						CASE WHEN ${COL.DOCTYPE} IN ('PI','CP') THEN TRY_CAST(${COL.QUANTITY} AS DOUBLE) ELSE 0 END as quantity,
						CASE WHEN ${COL.DOCTYPE} = 'SD' THEN TRY_CAST(${COL.AMOUNT} AS DOUBLE) ELSE 0 END as sd_amount,
						CASE WHEN ${COL.DOCTYPE} = 'SD' THEN TRY_CAST(${COL.QUANTITY} AS DOUBLE) ELSE 0 END as sd_quantity,
						0 as pr_amount, 0 as pr_quantity
					FROM ${purchaseTable} WHERE ${purchaseFilter}
					UNION ALL
					SELECT ${COL.ITEMCODE} as item_code,
						0 as amount, 0 as quantity, 0 as sd_amount, 0 as sd_quantity,
						TRY_CAST(${COL.AMOUNT} AS DOUBLE) as pr_amount,
						TRY_CAST(${COL.QUANTITY} AS DOUBLE) as pr_quantity
					FROM ${prTable} WHERE ${prFilter}
				) AS combined
				GROUP BY item_code
			`),
			queryHead.rawQuery(`
				SELECT ${COL.CODE} as item_code, "DESCRIPTION" as item_desc
				FROM ${stockTable}
				WHERE 1=1${stockCodeFilter}
			`),
		]);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.95);

		// Build stock description lookup
		const stockDescMap: Map<string, string> = new Map();
		for (const stock of stockItems) {
			stockDescMap.set(stock.item_code, stock.item_desc || '----');
		}

		// Build table items
		this.tableFields = [
			{ key: 'item_code', sortable: true },
			{ key: 'item_description', sortable: true },
			{ key: 'amount', sortable: true, formatter: TableItemFormatter.currencySplit },
			{ key: 'quantity', sortable: true, formatter: TableItemFormatter.basicInteger },
		];

		this.tableItems = itemData
			.map((row) => {
				const itemCode = row.item_code;
				const piCpAmount = Number(row.amount) || 0;
				const piCpQuantity = Number(row.quantity) || 0;
				const sdAmount = Number(row.sd_amount) || 0;
				const sdQuantity = Number(row.sd_quantity) || 0;

				const purchaseAmount = piCpAmount + (this.includeDebitNote ? sdAmount : 0);
				const purchaseQuantity = piCpQuantity + (this.includeDebitNote ? sdQuantity : 0);

				const prAmount = Number(row.pr_amount) || 0;
				const prQuantity = Number(row.pr_quantity) || 0;

				const itemAmount = this.includeCreditNote ? purchaseAmount - prAmount : purchaseAmount;
				const itemQuantity = this.includeCreditNote ? purchaseQuantity - prQuantity : purchaseQuantity;

				if (itemAmount === 0 && itemQuantity === 0) {
					return null;
				}

				return {
					item_code: itemCode,
					item_description: stockDescMap.get(itemCode) || '----',
					amount: itemAmount,
					quantity: itemQuantity,
				};
			})
			.filter((item) => item !== null);

		// Add AP Document row if in AP mode and there's a difference
		if (!this.slPhModuleOnly && apDocumentAmount !== 0) {
			this.tableItems.push({
				item_code: '*AP-Docs',
				item_description: 'Supplier Module Documents',
				amount: apDocumentAmount,
				quantity: 0,
			});
		}

		this.generateLoadingText(1);
		this.saveHistory('tableItems', 'tableFields');
	}

	public onHovering(value: boolean, filterId: string) {
		this.$emit('hovering', value, filterId);
	}
	public onEditing(value: boolean, filterId: string) {
		this.$emit('editing', value, filterId);
	}
}
